/* eslint-disable no-param-reassign */
import {
  useState, useEffect, useRef, Fragment,
} from 'react';
import PropTypes from 'prop-types';

import Button from '../../../juristec-ui/core/Button';
import ActionsGroup from '../../../juristec-ui/core/ActionsGroup';
import Tooltip from '../../../juristec-ui/core/Tooltip';
import IconButton from '../../../juristec-ui/core/IconButton';
import * as ops from '../../../utils/models/customColumnsOperations';
import uuidv4 from '../../../juristec-ui/utils/functions/randomUUID';
import { formatStrOpts, formatDateOpts, formatNumOpts } from '../../../options';

import {
  MainContainer,
  GridContainer,
  ColumnsList,
  ItemsWrapper,
  FormulaWrapper,
  LeftSide,
  RightSide,
  EmptyMsg,
  ItemOptions,
} from './styled/CustomColumns.styled';

import {
  DateIcon, Delete, Edit, Letters, Number,
} from '../../../juristec-ui/icons';

const icons = {
  abc: <Letters />,
  float: <Number />,
  date: <DateIcon />,
};

const customColumnsDefaultPropColumns = {};
const customColumnsDefaultPropCustom = [];

const CustomColumns = ({
  columns = customColumnsDefaultPropColumns,
  custom = customColumnsDefaultPropCustom,
  hide,
  submit,
  manageCustomColumns,
}) => {
  const [realColumns, setRealColumns] = useState(columns);
  const [realCustom, setRealCustom] = useState(custom);
  const [isDirty, setIsDirty] = useState(false);
  const dependencies = useRef(new Set());
  const [tempCColumns, setTempCColumns] = useState([]);

  const handleClose = () => {
    hide(!isDirty);
  };

  useEffect(() => {
    if (realCustom?.length > 0 && realColumns) {
      setTempCColumns(realCustom.map((cc) => (
        {
          ...cc,
          operator: ops[cc.operator],
          inputs: cc.inputs.map((inp) => {
            let inpObj = {
              id: uuidv4(),
              value: inp,
              label: inp,
              type: 'abc',
              origin: 'COLUMN',
            };
            if (typeof inp === 'object' && inp.column) {
              inpObj = inp;
            } else if (typeof inp === 'string') {
              if (realColumns[inp]) {
                inpObj.type = realColumns[inp];
                inpObj.origin = 'COLUMN';
                dependencies.current.add(inp);
              } else if (inp === 'TODAY' || /\d{4}-\d{2}-\d{2}/.test(inp)) {
                inpObj.type = 'date';
                inpObj.label = inp === 'TODAY' ? 'HOJE' : inp.split('-').reverse().join('/');
                inpObj.origin = inp === 'TODAY' ? 'TODAY' : 'DATE';
              } else if ([
                ...formatDateOpts,
                ...formatStrOpts,
                ...formatNumOpts,
              ].find((opt) => opt.value === inp)) {
                // inpObj.type = 'text';
                inpObj.origin = 'FORMAT';
              } else {
                // inpObj.type = 'text';
                inpObj.origin = 'TEXT';
              }
            } else if (typeof inp === 'number') {
              inpObj.type = 'float';
              inpObj.label = inp.toString().replace('.', ',');
              inpObj.origin = 'NUMBER';
            }
            return inpObj;
          }),
        }
      )));
    }
  }, [columns, realCustom]);

  const addCColumn = (newCColumn) => {
    setTempCColumns((cCols) => [...cCols, { ...newCColumn, status: 'NOVO' }]);
    newCColumn.inputs.forEach((inp) => {
      dependencies.current.add(inp.value ?? inp.column);
    });
    setIsDirty(true);
  };

  const editCColumn = (modifCColumn) => {
    dependencies.current = new Set();
    setTempCColumns((cCols) => cCols.reduce((aux, cCol) => {
      if (cCol.name === modifCColumn.name && cCol.operator !== 'REMOVE') {
        aux.push({
          ...modifCColumn,
          status: cCol.status ?? 'MODIFICADO',
        });
        modifCColumn.inputs.forEach((inp) => {
          dependencies.current.add(inp.value ?? inp.column);
        });
      } else {
        aux.push(cCol);
        cCol.inputs.forEach((inp) => {
          dependencies.current.add(inp.value ?? inp.column);
        });
      }
      return aux;
    }, []));
    setIsDirty(true);
  };

  const removeCColumn = (cColumn) => {
    dependencies.current = new Set();
    setTempCColumns((cCols) => cCols.reduce((aux, cCol) => {
      if (cCol.name !== cColumn.name) {
        aux.push(cCol);
        cCol.inputs.forEach((inp) => {
          dependencies.current.add(inp.value ?? inp.column);
        });
      } else if (!cCol.status) {
        aux.push({
          ...cCol,
          operator: 'REMOVE',
        });
      }
      return aux;
    }, []));
    setIsDirty(true);
  };

  const openAddCustomColumnsFormula = () => {
    manageCustomColumns({
      columns: realColumns, custom: tempCColumns, submit: addCColumn, mode: 'FORMULA',
    });
  };

  const openAddCustomColumnsMap = () => {
    manageCustomColumns({
      columns: realColumns, custom: tempCColumns, submit: addCColumn, mode: 'MAP',
    });
  };

  const openEditCustomColumns = (item, mode) => {
    manageCustomColumns({
      columns: realColumns, custom: tempCColumns, submit: editCColumn, editCColumn: item, mode,
    });
  };

  const saveCallback = () => {
    setIsDirty(false);
    setRealColumns((prev) => {
      const newColumns = { ...prev };
      tempCColumns.forEach((cCol) => {
        if (cCol.operator === 'REMOVE') {
          delete newColumns[cCol.name];
        } else {
          newColumns[cCol.name] = cCol.operator.type;
        }
      });
      return newColumns;
    });
    setRealCustom(tempCColumns.filter((cCol) => cCol.operator !== 'REMOVE').map((cCol) => ({
      name: cCol.name,
      operator: cCol.operator.value,
      inputs: cCol.operator?.value === 'GROUP_MAP' ? cCol.inputs : cCol.inputs.map((inp) => inp.value),
    })));
  };

  const submitColumns = () => {
    submit(tempCColumns.reduce((aux, c) => {
      aux.push({
        name: c.name ?? '',
        inputs: c.operator?.value === 'GROUP_MAP' ? c.inputs : c.inputs?.map((inp) => inp.value),
        operator: c.operator === 'REMOVE' ? 'REMOVE' : c.operator?.value ?? '',
      });
      return aux;
    }, []), saveCallback);
  };

  return (
    <MainContainer>
      <GridContainer>
        <LeftSide>
          <span>
            Crie colunas novas a partir de operações em colunas já existentes no arquivo.
          </span>
          <span className="obs">
            <b>Observação: </b>
            Alterações nas colunas customizadas utilizadas na costrução de algum indicador
            podem causar a invalidação do mesmo. Certifique que elas não estão em uso ou que
            a alteração é realmente necessária.
          </span>
          <div>
            <h5>Nova coluna:</h5>
            <Button
              color="secondary"
              onClick={openAddCustomColumnsFormula}
            >
              Por fórmula
            </Button>
            <Button
              color="secondary"
              onClick={openAddCustomColumnsMap}
            >
              Por depara
            </Button>
          </div>
        </LeftSide>
        <RightSide>
          <ColumnsList>
            {tempCColumns.length > 0 ? (
              tempCColumns.map((cCol) => {
                if (cCol.operator === 'REMOVE') return null;
                const isDependency = dependencies.current?.has(cCol.name);
                return (
                  <Fragment key={cCol.name}>
                    <ItemsWrapper varType={cCol.operator.type}>
                      {icons[cCol.operator.type]}
                    </ItemsWrapper>
                    <ItemsWrapper>
                      <h4 style={{ fontStyle: cCol?.status ? 'italic' : 'normal' }}>
                        {Boolean(cCol?.status) && <i>{`(${cCol.status}) `}</i>}
                        {cCol.name}
                      </h4>
                      <FormulaWrapper>
                        {cCol.operator.formulaView(cCol.inputs)}
                      </FormulaWrapper>
                    </ItemsWrapper>
                    <ItemsWrapper>
                      <ItemOptions>
                        <Tooltip text="Editar" atModal>
                          <IconButton
                            shape="rounded"
                            onClick={() => openEditCustomColumns(
                              cCol, cCol.operator?.value === 'GROUP_MAP' ? 'MAP' : 'FORMULA',
                            )}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          text={
                            isDependency ? 'Esta coluna é utilizada em outra coluna customizada' : 'Remover'
                          }
                          atModal
                        >
                          <IconButton
                            shape="rounded"
                            onClick={() => removeCColumn(cCol)}
                            disabled={isDependency}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </ItemOptions>
                    </ItemsWrapper>
                  </Fragment>
                );
              })
            ) : (
              <EmptyMsg>
                Nenhuma coluna customizada foi adicionada.
              </EmptyMsg>
            )}
          </ColumnsList>
        </RightSide>
      </GridContainer>
      <ActionsGroup>
        <Button
          style={{ margin: '5px' }}
          onClick={handleClose}
          variant="outlined"
          size="large"
        >
          {isDirty ? 'Cancelar' : 'Fechar'}
        </Button>
        <Button
          style={{ margin: '5px' }}
          size="large"
          onClick={submitColumns}
          disabled={!isDirty}
        >
          Salvar
        </Button>
      </ActionsGroup>
    </MainContainer>
  );
};

CustomColumns.propTypes = {
  columns: PropTypes.objectOf(PropTypes.string),
  custom: PropTypes.arrayOf(PropTypes.shape({})),
  hide: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  manageCustomColumns: PropTypes.func.isRequired,
};

export default CustomColumns;
